// React hooks
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// Tipovi i konstante
import { VehicleContractSaleCardProps, VehicleContractSaleRef } from "./types";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";

import VehicleContractEditCard from "../../../VehicleContractSaleEditCard/VehicleContractSaleEditCard";
import VehicleInvoiceSaleEditCard from "../../../VehicleInvoiceSaleEditCard/VehicleInvoiceSaleEditCard";
import { deleteFleetVehicleContractSaleAPI, getFleetVehicleContractSalesAPI } from "services/api/fleet/vehicles/contracts/api";
import { FleetVehicleContractSaleRow } from "services/api/fleet/vehicles/contracts/types";
import VehicleContractSaleSearchResults from "../VehicleContractSaleSearchResults/VehicleContractSaleSearchResults";
import { useNavigate } from "react-router-dom";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { Dialog } from "ui/components/Dialog";

const VehicleContractSaleCard = forwardRef<VehicleContractSaleRef, VehicleContractSaleCardProps>((props, ref) => {
  const { fleetVehicleDetail, onInvoiceCreated } = props;

  const { t } = useTranslate();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleContractSaleRow>
  >(initialDataWithPagination);

  const [contractId, setContractId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false);
  const [invoiceContractSaleId, setInvoiceContractSaleId] = useState<string>("");

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleContractSalesAPI(pagination, { vehicleId });
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    if (id) {
      setContractId(id);
      setShowAddNewModal(true);
    }
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setContractId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    setShowAddNewModal(false);
  }, [fleetVehicleDetail.id, searchResults.pagination]);

  const openAddNew = () => {
    setContractId("");
    setShowAddNewModal(true);
  };

  const refresh = () => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  };

  useImperativeHandle(ref, () => ({
    openAddNew,
    refresh,
  }));

  const handleDeleteClick = (event: React.MouseEvent<HTMLDivElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setContractId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!contractId || !fleetVehicleDetail.id) return;

    try {
      const deleted = await deleteFleetVehicleContractSaleAPI(
        fleetVehicleDetail?.id,
        contractId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
      }

      // zatvori dijalog nakon brisanja
      setContractId("");
      setOpenDialog(false);

    } catch (err) {
      console.error("Greška prilikom brisanja ugovora:", err);
    }
  }, [contractId, t, fleetVehicleDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setContractId("");
    setOpenDialog(false);
  }, []);

  const handleCreateInvoice = (contractSaleId: string) => {
    setShowAddNewModal(false);
    setInvoiceContractSaleId(contractSaleId);
    setShowInvoiceModal(true);
  };

  const handleInvoiceClose = () => {
    setShowInvoiceModal(false);
    setInvoiceContractSaleId("");
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    if (onInvoiceCreated) onInvoiceCreated();
  };


  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div></div>
          <div className="flex items-center gap-2 px-3 my-3">
            <Button
              size="small"
              variant="primary"
              children={t("admin.fleet.vehicle.contract.sale.search.add")}
              onClick={handleAddNew}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
        <div className="m-[-1px]">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.vehicle.contract.sale.search.noresults")} />
          ) : (
            <VehicleContractSaleSearchResults
              searchResults={searchResults}
              selectedRows={[]}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onContactClick={(e, id) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id }));
              }}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <VehicleContractEditCard
        show={showAddNewModal}
        vehicleId={fleetVehicleDetail.id}
        contractId={contractId}
        onSuccess={handleEditSuccess}
        onClose={handleEditSuccess}
        onCreateInvoice={handleCreateInvoice}
      />
      <VehicleInvoiceSaleEditCard
        show={showInvoiceModal}
        vehicleId={fleetVehicleDetail.id}
        contractSaleId={invoiceContractSaleId}
        onSuccess={handleInvoiceClose}
        onClose={handleInvoiceClose}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.contract.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.contract.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.contract.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.contract.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(VehicleContractSaleCard);